import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { adminApi } from '@/api/endpoints'
import { Avatar, BackIcon, ChevronIcon, RetryIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, PageSpinner, Skeleton } from '@/components/ui/Feedback'
import { Modal } from '@/components/ui/Modal'
import { StatStrip } from '@/components/ui/StatStrip'
import { ApplicationStatusPill, StatusPill } from '@/components/ui/StatusPill'
import { LiveQueueList } from '@/features/live/LiveQueueList'
import { LIVE_KEY, clock, hostOf, useLiveQueue } from '@/features/live/live'
import { ProfilePanel } from '@/features/users/ProfilePanel'
import { SubscriptionPanel, applicationsLeft } from '@/features/users/SubscriptionPanel'
import { cn, formatDate, humanise, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { ApplicationFilter, ApplicationRow, UserDetail, ScreeningQuestion } from '@/types/api'

const BULK = 3
const PAGE_SIZE = 20
const APPLICATION_FILTERS: Array<{ value: ApplicationFilter | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_flight', label: 'Applying' },
  { value: 'queued', label: 'Queued' },
  { value: 'needs_you', label: 'Needs user' },
  { value: 'failed', label: 'Failed' },
  { value: 'captcha_stopped', label: 'CAPTCHA stopped' },
]

/** What "run again" is about to do; null when nothing is being confirmed. */
type Confirming = { applicationId: string; title: string } | { bulk: number } | null

/**
 * One user: what is waiting for a person right now, what a CAPTCHA stopped
 * (and the way to run it again for them), then every application.
 *
 * Read-mostly on purpose. The two things an administrator can do here are the
 * two the user could do themselves: clear a live check, and retry what a
 * CAPTCHA stopped. Both are noted on the user's timeline.
 */
export function UserDetailPage() {
  const { userId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const queue = useLiveQueue()
  const [confirming, setConfirming] = useState<Confirming>(null)
  // Which half of the page is open lives in the address, so a link can point at the profile.
  const [params, setParams] = useSearchParams()
  const tab: 'applications' | 'profile' = params.get('tab') === 'profile' ? 'profile' : 'applications'
  const openTab = (next: 'applications' | 'profile') => setParams(next === 'profile' ? { tab: 'profile' } : {}, { replace: true })

  const user = useQuery({ queryKey: ['user', userId], queryFn: () => adminApi.user(userId), refetchInterval: 10_000 })
  const overview = useQuery({ queryKey: ['overview'], queryFn: adminApi.overview, staleTime: 60_000 })
  const timeout = overview.data?.captcha.timeoutSeconds ?? null
  const workerDown = overview.data?.system.applyWorkers === 0

  const retry = useMutation({
    mutationFn: (body: { applicationId?: string; limit?: number }) => adminApi.retryCaptchaStopped(userId, body),
    onSuccess: ({ queued }) => {
      setConfirming(null)
      void queryClient.invalidateQueries({ queryKey: ['user', userId] })
      void queryClient.invalidateQueries({ queryKey: ['user-applications', userId] })
      void queryClient.invalidateQueries({ queryKey: LIVE_KEY })
      if (!queued.length) {
        toast.info('Nothing to run', 'None of them could be run again.')
        return
      }
      toast.success(
        queued.length === 1 ? 'Running it again' : `Running ${queued.length} again`,
        workerDown ? 'The apply worker is not running, so they will wait in the queue.' : 'Stay on Live checks: each CAPTCHA shows there when its run reaches it.',
      )
      if (!workerDown) navigate('/live')
    },
    onError: (error) => {
      setConfirming(null)
      toast.error('Could not run it again', error instanceof ApiError ? error.message : undefined)
    },
  })

  if (user.isLoading) return <PageSpinner label="Loading the user" />
  if (user.isError || !user.data) {
    const missing = user.error instanceof ApiError && (user.error.status === 404 || user.error.status === 422)
    return (
      <div className="card">
        <ErrorState title={missing ? 'No such user' : 'The user could not be loaded'} description={missing ? 'That link does not match an account.' : undefined} onRetry={missing ? undefined : () => void user.refetch()} />
      </div>
    )
  }

  const data = user.data
  const name = data.fullName || data.email
  // The shared queue ticks every second; the detail's own copy is only as fresh as its last load.
  const live = queue.items.filter((item) => item.user?.id === data.id)
  const stopped = data.captchaStopped

  return (
    <>
      <Link to="/users" className="mb-3 inline-flex h-9 items-center gap-1.5 rounded-field pl-1.5 pr-3 text-[13.5px] font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink">
        <BackIcon className="h-4 w-4" />
        Users
      </Link>

      <header className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3">
        <Avatar name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="min-w-0 truncate text-[21px] font-semibold tracking-[-0.02em] text-ink sm:text-[25px]">{name}</h1>
            {data.role === 'admin' && <StatusPill tone="brand">Admin</StatusPill>}
            <StatusPill tone={data.status === 'active' ? 'success' : 'neutral'}>{humanise(data.status)}</StatusPill>
          </div>
          <p className="mt-0.5 truncate text-[13.5px] text-ink-muted">
            {data.fullName && <span>{data.email} · </span>}
            Joined {formatDate(data.createdAt)} · {data.lastLoginAt ? `seen ${relativeTime(data.lastLoginAt)}` : 'never signed in'}
          </p>
        </div>
      </header>

      <StatStrip
        className="mb-5"
        segments={[
          { key: 'submitted', label: 'Submitted', value: data.counts.submitted, tone: 'success' },
          { key: 'in_progress', label: 'In progress', value: data.counts.inProgress, tone: 'info', live: data.counts.inProgress > 0 },
          { key: 'needs_you', label: 'Needs user', value: data.counts.needsYou, tone: 'warning' },
          { key: 'failed', label: 'Failed', value: data.counts.failed, tone: 'danger', hint: data.counts.captchaStopped ? `${data.counts.captchaStopped} by a CAPTCHA` : undefined },
        ]}
      />

      <div role="tablist" aria-label="Sections" className="mb-5 flex gap-1 border-b border-line">
        {(
          [
            ['applications', 'Applications', data.counts.total],
            ['profile', 'Profile', null],
          ] as const
        ).map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => openTab(value)}
            className={cn(
              '-mb-px flex h-10 items-center gap-2 border-b-2 px-3 text-[14px] font-medium transition-colors',
              tab === value ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {label}
            {count !== null && <span className="num text-[12px] text-ink-muted">{count}</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        {tab === 'profile' ? (
          <div className="min-w-0">
            <ProfilePanel userId={data.id} email={data.email} />
          </div>
        ) : (
        <div className="min-w-0 space-y-5">
          {/* ---- Waiting right now ---- */}
          {live.length > 0 && (
            <section className="card overflow-hidden border-warning/40">
              <header className="flex items-center gap-2.5 border-b border-line bg-warning/[0.07] px-4 py-3.5 sm:px-5">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
                </span>
                <h2 className="text-[15.5px] font-semibold text-ink">{live.length === 1 ? 'A check is waiting right now' : `${live.length} checks are waiting right now`}</h2>
              </header>
              <LiveQueueList items={live} />
            </section>
          )}

          {/* ---- Stopped by a CAPTCHA ---- */}
          <section className="card overflow-hidden">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
              <div className="min-w-0">
                <h2 className="text-[15.5px] font-semibold text-ink">
                  Stopped by a CAPTCHA
                  {stopped.length > 0 && <span className="num ml-2 text-[13px] font-medium text-ink-muted">{data.counts.captchaStopped}</span>}
                </h2>
                <p className="mt-0.5 text-[12.5px] text-ink-muted">Nothing was sent on these. Run one again and its CAPTCHA comes up on Live checks for you or the user to clear.</p>
              </div>
              {stopped.length > 1 && (
                <Button variant="secondary" size="sm" icon={<RetryIcon className="h-4 w-4" />} onClick={() => setConfirming({ bulk: Math.min(BULK, stopped.length) })}>
                  Run the latest {Math.min(BULK, stopped.length)}
                </Button>
              )}
            </header>
            {stopped.length === 0 ? (
              <p className="px-4 py-6 text-[13.5px] text-ink-muted sm:px-5">None. No CAPTCHA has stopped this user's applications.</p>
            ) : (
              <ul className="divide-y divide-line">
                {stopped.map((application) => (
                  <li key={application.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-medium text-ink">{application.jobTitle ?? 'Untitled job'}</p>
                      <p className="truncate text-[12.5px] text-ink-muted">
                        {[application.companyName, hostOf(application.applyUrl), application.lastStatusAt && relativeTime(application.lastStatusAt)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setConfirming({ applicationId: application.id, title: application.jobTitle ?? 'this application' })}
                      className="shrink-0"
                    >
                      Run again
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <Applications userId={data.id} />
        </div>
        )}

        <Side data={data} />
      </div>

      <Modal
        open={confirming !== null}
        onClose={() => !retry.isPending && setConfirming(null)}
        size="sm"
        title={confirming && 'bulk' in confirming ? `Run the latest ${confirming.bulk} again?` : 'Run this application again?'}
        description={`For ${name}.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)} disabled={retry.isPending}>
              Cancel
            </Button>
            <Button
              loading={retry.isPending}
              onClick={() => confirming && retry.mutate('bulk' in confirming ? { limit: confirming.bulk } : { applicationId: confirming.applicationId })}
            >
              Run again
            </Button>
          </>
        }
      >
        <ul className="space-y-2.5 text-[13.5px] leading-relaxed text-ink-soft">
          {confirming && 'applicationId' in confirming && (
            <li>
              <span className="font-medium text-ink">{confirming.title}</span> is applied for again from the start, with the user's own profile and answers.
            </li>
          )}
          <li>
            If the site shows its CAPTCHA again, it appears on Live checks{timeout ? ` and waits ${clock(timeout)}` : ''}. Be ready to clear it
            {confirming && 'bulk' in confirming ? ': several can come up close together.' : '.'}
          </li>
          <li>Once the check is clear the application is submitted to the employer as the user set it up.</li>
          <li>The user sees on their timeline that support ran it.</li>
          {workerDown && <li className="font-medium text-danger">The apply worker is not running. It will sit in the queue until one starts.</li>}
        </ul>
      </Modal>
    </>
  )
}

// ---- Applications ---------------------------------------------------------------------
function Applications({ userId }: { userId: string }) {
  const [status, setStatus] = useState<ApplicationFilter | ''>('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<string | null>(null)
  const applications = useQuery({
    queryKey: ['user-applications', userId, { status, page }],
    queryFn: () => adminApi.userApplications(userId, { status, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  })
  const rows = applications.data?.data ?? []
  const info = applications.data?.pageInfo

  return (
    <section className="card overflow-hidden">
      <header className="border-b border-line px-4 py-3.5 sm:px-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[15.5px] font-semibold text-ink">Applications</h2>
          {info && <span className="num text-[12.5px] text-ink-muted">{info.totalItems}</span>}
        </div>
        <div className="hscroll -mx-4 mt-3 gap-1.5 px-4 sm:-mx-5 sm:px-5" role="radiogroup" aria-label="Status">
          {APPLICATION_FILTERS.map((option) => (
            <button
              key={option.value || 'all'}
              type="button"
              role="radio"
              aria-checked={status === option.value}
              onClick={() => {
                setStatus(option.value)
                setPage(1)
                setOpen(null)
              }}
              className={cn(
                'h-8 shrink-0 rounded-pill border px-3 text-[12.5px] font-medium transition-colors',
                status === option.value ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      {applications.isLoading ? (
        <div className="space-y-3 p-5">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-11 w-full" />
          ))}
        </div>
      ) : applications.isError ? (
        <ErrorState title="Applications could not be loaded" onRetry={() => void applications.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No applications" description={status ? 'None with this status.' : 'This user has not applied to anything yet.'} />
      ) : (
        <ul className={cn('divide-y divide-line transition-opacity', applications.isPlaceholderData && 'opacity-60')}>
          {rows.map((application) => (
            <ApplicationItem key={application.id} userId={userId} application={application} open={open === application.id} onToggle={() => setOpen(open === application.id ? null : application.id)} />
          ))}
        </ul>
      )}

      {info && info.totalPages > 1 && (
        <nav aria-label="Pages" className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 sm:px-5">
          <Button variant="secondary" size="xs" disabled={!info.hasPrevious} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="num text-[12.5px] text-ink-muted">
            {info.page} / {info.totalPages}
          </span>
          <Button variant="secondary" size="xs" disabled={!info.hasNext} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </nav>
      )}
    </section>
  )
}

function ApplicationItem({ userId, application, open, onToggle }: { userId: string; application: ApplicationRow; open: boolean; onToggle: () => void }) {
  return (
    <li>
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium text-ink">{application.jobTitle ?? 'Untitled job'}</p>
          <p className="truncate text-[12.5px] text-ink-muted">
            {[application.companyName, application.portalType && humanise(application.portalType), relativeTime(application.lastStatusAt ?? application.createdAt)].filter(Boolean).join(' · ')}
          </p>
        </div>
        {application.captchaStopped ? (
          <StatusPill tone="danger" size="sm">
            CAPTCHA stopped
          </StatusPill>
        ) : (
          <ApplicationStatusPill status={application.status} needsReview={application.needsReview} size="sm" />
        )}
        <ChevronIcon className={cn('h-4 w-4 shrink-0 text-ink-faint transition-transform', open && 'rotate-90')} />
      </button>
      {open && application.status === 'needs_you' && <QuestionsPanel userId={userId} application={application} />}
      {open && <Timeline application={application} />}
    </li>
  )
}

// ---- Answering for the candidate -------------------------------------------------------
/**
 * The questions an application in "Needs you" waits on, answerable here on the
 * candidate's behalf (the same path as their own review screen). "Save and
 * send" queues the application; "Save" keeps the answers for the next run.
 */
function QuestionsPanel({ userId, application }: { userId: string; application: ApplicationRow }) {
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: ['admin-application-detail', userId, application.id],
    queryFn: () => adminApi.applicationDetail(userId, application.id),
  })
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const questions = detail.data?.screeningQuestions ?? []
  const open = questions.filter((q) => !(q.answerValue || '').trim())
  const valueOf = (q: ScreeningQuestion) => drafts[q.questionKey] ?? q.answerValue ?? q.suggestedValue ?? ''
  const save = useMutation({
    mutationFn: (submit: boolean) =>
      adminApi.submitScreening(userId, application.id, {
        answers: questions
          .filter((q) => valueOf(q).trim() !== (q.answerValue || '').trim())
          .map((q) => ({ questionKey: q.questionKey, answerValue: valueOf(q).trim() || null })),
        submit,
      }),
    onSuccess: (_data, submit) => {
      toast.success(submit ? 'Answers saved — the application is queued' : 'Answers saved for the candidate')
      void queryClient.invalidateQueries({ queryKey: ['admin-application-detail', userId, application.id] })
      void queryClient.invalidateQueries({ queryKey: ['user-applications', userId] })
      void queryClient.invalidateQueries({ queryKey: ['application-events', application.id] })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Could not save the answers'),
  })
  const missing = open.filter((q) => q.isRequired && !valueOf(q).trim()).length
  if (detail.isLoading) return <div className="border-t border-line px-4 py-4 sm:px-5"><Skeleton className="h-12 w-full" /></div>
  if (detail.isError) return <p className="border-t border-line px-4 py-3 text-[13px] text-ink-muted sm:px-5">The questions could not be loaded.</p>
  if (!questions.length) {
    return (
      <p className="border-t border-line px-4 py-3 text-[13px] text-ink-muted sm:px-5">
        No questions were captured for this stop — the timeline below says where it stopped.
      </p>
    )
  }
  return (
    <div className="border-t border-line px-4 py-4 sm:px-5">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-[13.5px] font-semibold text-ink">
          {open.length ? `${open.length} question${open.length === 1 ? '' : 's'} waiting` : 'Every question answered'}
        </h3>
        <span className="text-[12px] text-ink-faint">Answering here answers for the candidate</span>
      </div>
      <ul className="space-y-3">
        {questions.map((q) => {
          const current = valueOf(q)
          const choices = q.options.filter((option) => option.trim())
          return (
            <li key={q.questionKey} className={cn('rounded-field border px-3 py-2.5', (q.answerValue || '').trim() ? 'border-line' : 'border-warning/50 bg-warning/[0.05]')}>
              <label className="block text-[13px] font-medium text-ink" htmlFor={`q-${q.id}`}>
                {q.questionText}
                {q.isRequired && <span className="text-danger"> *</span>}
              </label>
              {q.rationale && <p className="mt-0.5 text-[12px] text-ink-muted">{q.rationale}</p>}
              {choices.length && q.questionType !== 'multi_select' ? (
                <select
                  id={`q-${q.id}`}
                  value={current}
                  onChange={(event) => setDrafts({ ...drafts, [q.questionKey]: event.target.value })}
                  className="mt-1.5 h-9 w-full rounded-field border border-line bg-surface px-2 text-[13px] text-ink"
                >
                  <option value="">— choose —</option>
                  {choices.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <textarea
                  id={`q-${q.id}`}
                  value={current}
                  rows={current.length > 80 ? 3 : 1}
                  placeholder={choices.length ? `Comma-separated: ${choices.slice(0, 4).join(', ')}${choices.length > 4 ? '…' : ''}` : 'Type the answer'}
                  onChange={(event) => setDrafts({ ...drafts, [q.questionKey]: event.target.value })}
                  className="mt-1.5 w-full rounded-field border border-line bg-surface px-2 py-1.5 text-[13px] text-ink"
                />
              )}
            </li>
          )
        })}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={save.isPending || missing > 0} onClick={() => save.mutate(true)}>
          {save.isPending ? 'Saving…' : 'Save and send'}
        </Button>
        <Button variant="secondary" size="sm" disabled={save.isPending} onClick={() => save.mutate(false)}>
          Save only
        </Button>
        {missing > 0 && <span className="text-[12.5px] text-ink-muted">{missing} required still empty</span>}
      </div>
    </div>
  )
}

function Timeline({ application }: { application: ApplicationRow }) {
  const events = useQuery({ queryKey: ['application-events', application.id], queryFn: () => adminApi.applicationEvents(application.id) })
  return (
    <div className="border-t border-line bg-surface-muted/60 px-4 py-4 sm:px-5">
      {application.failureReason && (
        <p className="mb-3 rounded-field bg-danger/[0.07] px-3 py-2 text-[13px] leading-relaxed text-danger">{application.failureReason}</p>
      )}
      {events.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : events.isError ? (
        <p className="text-[13px] text-ink-muted">The timeline could not be loaded.</p>
      ) : !events.data?.length ? (
        <p className="text-[13px] text-ink-muted">Nothing on the timeline yet.</p>
      ) : (
        <ol className="space-y-2.5">
          {events.data.map((event) => {
            const support = event.eventType.startsWith('admin_')
            const captcha = event.eventType.startsWith('captcha_')
            return (
              <li key={event.id} className="flex gap-3 text-[13px]">
                <span className={cn('mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full', support ? 'bg-brand' : captcha ? 'bg-warning' : 'bg-ink-faint')} />
                <div className="min-w-0 flex-1">
                  <p className={cn('leading-relaxed', support ? 'font-medium text-brand' : 'text-ink-soft')}>{event.message || humanise(event.eventType)}</p>
                  <p className="num text-[11.5px] text-ink-faint" title={new Date(event.createdAt).toLocaleString()}>
                    {relativeTime(event.createdAt)}
                  </p>
                </div>
              </li>
            )
          })}
        </ol>
      )}
      {application.applyUrl && (
        <p className="num mt-3 truncate border-t border-line pt-3 text-[12px] text-ink-faint" title={application.applyUrl}>
          {hostOf(application.applyUrl)} · {application.attempts} {application.attempts === 1 ? 'attempt' : 'attempts'}
        </p>
      )}
    </div>
  )
}

// ---- The side column: who this is and how their account is set up ---------------------
function Side({ data }: { data: UserDetail }) {
  const { profile, settings } = data
  return (
    <aside className="min-w-0 space-y-5">
      <SubscriptionPanel user={data} />

      <section className="panel divide-y divide-line">
        <p className="label-eyebrow px-4 py-3">Profile</p>
        <Fact label="Role">{profile?.currentTitle || profile?.headline || '–'}</Fact>
        <Fact label="Company">{profile?.currentCompany || '–'}</Fact>
        <Fact label="Location">{profile?.location || '–'}</Fact>
        <Fact label="Joined">{formatDate(data.createdAt)}</Fact>
        <Fact label="Last seen">{data.lastLoginAt ? relativeTime(data.lastLoginAt) : 'Never'}</Fact>
        <Fact label="Onboarding">{data.onboardingComplete ? 'Complete' : <span className="text-warning">Not finished</span>}</Fact>
        <Fact label="Profile complete">
          {data.profilePercent === null ? (
            '–'
          ) : (
            <span className="num" title={data.profileMissing.length ? `Missing: ${data.profileMissing.join(', ')}` : undefined}>
              {data.profilePercent}%
            </span>
          )}
        </Fact>
        {data.profileMissing.length > 0 && (
          <p className="px-4 py-2.5 text-[12px] leading-relaxed text-ink-muted">Missing: {data.profileMissing.join(', ')}</p>
        )}
        <Fact label="Applied">
          <span className="num">{data.counts.total}</span> · <span className="num text-success">{data.counts.submitted}</span> sent
        </Fact>
        <Fact label="Last applied">{data.lastAppliedAt ? relativeTime(data.lastAppliedAt) : '–'}</Fact>
        <Fact label="Applications left">{applicationsLeft(data.subscription)}</Fact>
      </section>

      <section className="panel divide-y divide-line">
        <p className="label-eyebrow px-4 py-3">Apply settings</p>
        {settings ? (
          <>
            <Fact label="Auto apply">{settings.autoApplyEnabled ? <span className="text-brand">On</span> : 'Off'}</Fact>
            <Fact label="Review before submit">{settings.reviewBeforeSubmit ? 'Yes' : 'No'}</Fact>
            <Fact label="Daily limit">
              <span className="num">{settings.dailyApplicationLimit}</span>
            </Fact>
            <Fact label="Minimum match">
              <span className="num">{settings.minMatchScore}</span>
            </Fact>
            <Fact label="Application password">{settings.hasApplicationPassword ? 'Set' : <span className="text-warning">Not set</span>}</Fact>
          </>
        ) : (
          <p className="px-4 py-3 text-[13px] text-ink-muted">Not set up yet.</p>
        )}
        <Fact label="Apply address">{data.mailAlias ? <span className="num break-all text-[12.5px]">{data.mailAlias}</span> : '–'}</Fact>
      </section>

      <section className="panel divide-y divide-line">
        <p className="label-eyebrow px-4 py-3">Job portals</p>
        {data.portals.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-ink-muted">None connected.</p>
        ) : (
          data.portals.map((portal) => (
            <div key={portal.key} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-[13.5px] font-medium text-ink">{humanise(portal.key)}</span>
                <StatusPill size="sm" tone={portal.status === 'connected' ? 'success' : portal.status === 'error' ? 'danger' : 'neutral'}>
                  {humanise(portal.status)}
                </StatusPill>
              </div>
              <p className="mt-0.5 truncate text-[12px] text-ink-muted">
                <span className="num">{portal.jobsSynced}</span> jobs · {portal.lastSyncedAt ? `synced ${relativeTime(portal.lastSyncedAt)}` : 'never synced'}
              </p>
              {portal.lastError && <p className="mt-1 line-clamp-2 text-[12px] text-danger">{portal.lastError}</p>}
            </div>
          ))
        )}
      </section>
    </aside>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <span className="shrink-0 text-[12.5px] text-ink-muted">{label}</span>
      <span className="min-w-0 text-right text-[13.5px] text-ink-soft">{children}</span>
    </div>
  )
}
